import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { 
  Mail, 
  Linkedin, 
  Github, 
  MapPin, 
  Phone, 
  Send,
  Heart,
  Coffee,
  Briefcase,
  FileText
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const Contact = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const sectionRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast({
          title: "Message Sent!",
          description: data.message,
          duration: 5000,
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      } else {
        // Handle validation errors or other issues
        const errorMessage = data.error || data.detail?.error || "Failed to send message. Please try again.";
        const errorDetails = data.details || data.detail?.details || [];
        
        toast({
          title: "Error",
          description: errorDetails.length > 0 ? errorDetails.join(', ') : errorMessage,
          variant: "destructive",
          duration: 5000,
        });
      }
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please check your connection and try again.",
        variant: "destructive",  
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      label: "Email",
      value: "shivendu0103@gmail.com",
      href: "mailto:shivendu0103@gmail.com",
      color: "text-red-600 dark:text-red-400"
    },
    {
      icon: Linkedin,
      label: "LinkedIn",
      value: "linkedin.com/in/shivendu-sinha-291163328",
      href: "https://linkedin.com/in/shivendu-sinha-291163328",
      color: "text-blue-600 dark:text-blue-400"
    },
    {
      icon: Github,
      label: "GitHub",
      value: "github.com/shivendu0103",
      href: "https://github.com/shivendu0103",
      color: "text-gray-800 dark:text-gray-400"
    },
    {
      icon: MapPin,
      label: "Location",
      value: "Chandigarh, India",
      href: "#",
      color: "text-green-600 dark:text-green-400"
    }
  ];

  const availability = [
    {
      icon: Briefcase,
      title: "Internships",
      description: "Open to full-time and part-time opportunities",
      status: "Available"
    },
    {
      icon: Coffee,
      title: "Freelance Projects",
      description: "Web development and AI/ML consulting",
      status: "Available"
    },
    {
      icon: Heart,
      title: "Collaborations",
      description: "Open source projects and hackathons",
      status: "Always Open"
    }
  ];

  return (
    <>
      <section id="contact" ref={sectionRef} className="py-20 bg-slate-50 dark:bg-slate-800 transition-colors duration-300">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 dark:text-white mb-4">
              Let's <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Connect</span>
            </h2>
            <p className="text-xl text-slate-600 dark:text-slate-300 max-w-3xl mx-auto">
              Ready to build something amazing together? I'm always excited to discuss new opportunities and ideas!
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div className={`space-y-8 transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'}`}>
              {/* Contact Details */}
              <Card className="hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Mail className="w-5 h-5 text-blue-600 dark:text-cyan-400" />
                    <span>Get in Touch</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {contactInfo.map((contact, index) => (
                    <a
                      key={contact.label}
                      href={contact.href}
                      target={contact.href.startsWith('http') ? '_blank' : '_self'}
                      rel={contact.href.startsWith('http') ? 'noopener noreferrer' : ''}
                      className={`flex items-center space-x-4 p-3 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors duration-200 group ${
                        contact.href === '#' ? 'cursor-default' : 'cursor-pointer'
                      }`}
                    >
                      <div className={`p-2 rounded-lg bg-slate-100 dark:bg-slate-700 group-hover:scale-110 transition-transform duration-200`}>
                        <contact.icon className={`w-5 h-5 ${contact.color}`} />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">{contact.label}</p>
                        <p className="text-sm text-slate-600 dark:text-slate-300">{contact.value}</p>
                      </div>
                    </a>
                  ))}
                </CardContent>
              </Card>

              {/* Availability Status */}
              <Card className="hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Briefcase className="w-5 h-5 text-green-600 dark:text-green-400" />
                    <span>Current Availability</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {availability.map((item, index) => (
                    <div key={item.title} className="flex items-start space-x-4">
                      <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
                        <item.icon className="w-5 h-5 text-green-600 dark:text-green-400" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-slate-900 dark:text-white">{item.title}</h4>
                          <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                            {item.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Quick CTA */}
              <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-950/30 dark:to-cyan-950/30 p-6 rounded-xl border border-blue-100 dark:border-blue-800">
                <h3 className="font-bold text-slate-900 dark:text-white mb-2">
                  🚀 Ready to start your next project?
                </h3>
                <p className="text-slate-600 dark:text-slate-300 text-sm mb-4">
                  I'm passionate about working on meaningful projects that make a real impact. 
                  Let's discuss how we can bring your ideas to life!
                </p>
                <Button
                  onClick={() => window.open('mailto:shivendu0103@gmail.com', '_blank')}
                  size="sm"
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Send Quick Email
                </Button>
              </div>
            </div>

            {/* Contact Form */}
            <div className={`transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
              <Card className="hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Send className="w-5 h-5 text-blue-600 dark:text-cyan-400" />
                    <span>Send a Message</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          Name *
                        </label>
                        <Input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Your full name"
                          required
                          className="transition-all duration-200 focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                          Email *
                        </label>
                        <Input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="your.email@example.com"
                          required
                          className="transition-all duration-200 focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Subject *
                      </label>
                      <Input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleInputChange}
                        placeholder="What would you like to discuss?"
                        required
                        className="transition-all duration-200 focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                        Message *
                      </label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Tell me about your project, ideas, or just say hello!"
                        rows={6}
                        required
                        className="transition-all duration-200 focus:ring-2 focus:ring-blue-500 resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white py-3 font-semibold transition-all duration-300 hover:scale-105 disabled:hover:scale-100"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Send Message
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 dark:bg-slate-950 text-white py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Brand */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                  Shivendu Sinha
                </span>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed">
                Computer Science student passionate about creating technology solutions 
                that make a real difference in the world.
              </p>
            </div>

            {/* Quick Links */}
            <div className="space-y-4">
              <h3 className="font-semibold text-white">Quick Links</h3>
              <div className="space-y-2">
                {['About', 'Projects', 'Skills', 'Contact'].map((link) => (
                  <button
                    key={link}
                    onClick={() => {
                      const element = document.getElementById(link.toLowerCase());
                      if (element) element.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="block text-slate-400 hover:text-cyan-400 transition-colors duration-200 text-sm"
                  >
                    {link}
                  </button>
                ))}
              </div>
            </div>

            {/* Social & Resume */}
            <div className="space-y-4">
              <h3 className="font-semibold text-white">Connect & Download</h3>
              <div className="flex space-x-4">
                <a
                  href="https://linkedin.com/in/shivendu-sinha-291163328"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-slate-800 rounded-lg hover:bg-blue-600 transition-colors duration-200"
                >
                  <Linkedin className="w-5 h-5" />
                </a>
                <a
                  href="https://github.com/shivendu0103"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 bg-slate-800 rounded-lg hover:bg-gray-600 transition-colors duration-200"
                >
                  <Github className="w-5 h-5" />
                </a>
                <a
                  href="mailto:shivendu0103@gmail.com"
                  className="p-2 bg-slate-800 rounded-lg hover:bg-red-600 transition-colors duration-200"
                >
                  <Mail className="w-5 h-5" />
                </a>
              </div>
              <Button
                onClick={async () => {
                  try {
                    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/resume/download`);
                    
                    if (response.ok) {
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.style.display = 'none';
                      a.href = url;
                      a.download = 'Shivendu_Sinha_Resume.pdf';
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      document.body.removeChild(a);
                    } else {
                      throw new Error('Resume not available');
                    }
                  } catch (error) {
                    console.error('Resume download failed:', error);
                    alert('Resume download is temporarily unavailable. Please contact me directly!');
                  }
                }}
                variant="outline"
                size="sm"
                className="text-white border-slate-600 hover:bg-slate-800"
              >
                <FileText className="w-4 h-4 mr-2" />
                Download Resume
              </Button>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-slate-800 mt-8 pt-8 text-center">
            <p className="text-slate-400 text-sm">
              © 2025 Shivendu Sinha. Built with React & ❤️ for innovation.
            </p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Contact;